import type { Plugin, UserConfig } from 'vite'
import { fileURLToPath } from 'url'
import { default as path, dirname } from 'path'
import { glob } from 'glob'
import { merge } from 'ts-deepmerge'

const coreAlias = '@auicore'
// 避免使用 import.meta.url，在构建时会被转换为 data URL
const __filename = fileURLToPath(import.meta.url)
const __dirname = dirname(__filename)

// 自定义AUI.vue文件处理插件
function auiVuePlugin(options: AuiOptions): Plugin {
  return {
    name: 'aui-vue-plugin',
    enforce: 'post', // 在Vue插件之后执行
    transform(code, id) {
      if (id.endsWith(`.aui.vue`)) {
        const fileName = path.basename(id).replace('.aui.vue', '');
        const sp = code.split('export default');
        const last = `
const __aui_comp_define__ = ${sp.pop()};
import __aui_como_core__ from '${options.coreAlias?options.coreAlias:'agile-core'}';
if(!__aui_comp_define__.name) {
  __aui_comp_define__.name = '${fileName}';
}
export const componentStructure = __aui_comp_define__;
export default __aui_como_core__.define(__aui_comp_define__);
`;
        const first = sp.join('export default');
        return {
          code: first + last,
        }
      }
      return null
    },
  }
}


const IMPORT_PREFIX = 'Comp';

function joinExport(files: string[]): string {
  const codes: string[] = [];
  files.forEach((_, index: number) => {
    codes.push(`[${IMPORT_PREFIX}${index}_${index}.name]: ${IMPORT_PREFIX}${index}`);
  });
  return codes.join(', ');
}

const INSERT_FLAG = '// insert';

function auiExportPlugin(): Plugin {
  return {
    name: 'aui-export-plugin',
    enforce: 'pre',
    transform(code, id) {
      if (id.endsWith(`.export.aui.ts`)) {

        if (code.indexOf(INSERT_FLAG) === -1) {
          throw new Error(`.export.aui.ts文件必须包含[ ${INSERT_FLAG} ]标记`);
        }
        const cwd = path.dirname(id);
        const files: string[] = glob.sync('**/*.aui.vue', {
          cwd,
        });

        if (files.length === 0) return null;

        const codes: string[] = [];
        files.forEach((file, index) => {
          const filePath = path.join(cwd, file).replace(/\\/g, '/');
          codes.push(`import ${IMPORT_PREFIX}${index} from '${filePath}';`);
          codes.push(`import {componentStructure as ${IMPORT_PREFIX}${index}_${index} } from '${filePath}';`);
        });
        codes.push(code.replace(INSERT_FLAG, joinExport(files)));
        return { code: codes.join(`\n`) };
      }
      return null
    },
  }
}


function auiConfigPlugin(options?: AuiOptions) {
  const virtualModuleId = 'virtual:aui-config'
  const resolvedVirtualModuleId = '\0' + virtualModuleId

  return {
    name: 'aui-config-plugin',
    resolveId(id: string) {
      if (id === virtualModuleId) {
        return resolvedVirtualModuleId
      }
    },
    load(id: string) {
      if (id === resolvedVirtualModuleId) {
        const auiConfig = merge({
          tagPrefix: 'aui',
          buildTime: new Date().toISOString()
        }, options || {})
        return `export default ${JSON.stringify(auiConfig)}`
      }
    }
  }
}

export type AuiOptions = {
  tagPrefix?: string,
  coreAlias?: string,
};

export type AuiVuePlugin = {
  userConfig: UserConfig,
  isCustomElement: (tag: string) => boolean,
};

// 导出AUI配置函数
export function getAuiConfig(options?: AuiOptions): AuiVuePlugin {

  options = merge({
    tagPrefix: 'aui'
  }, options || {});

  const tagPrefix = options?.tagPrefix || 'aui';

  return {
    userConfig: {
      plugins: [auiExportPlugin(), auiVuePlugin(options), auiConfigPlugin(options)],
      resolve: {
        alias: {
          [`${coreAlias}`]: path.resolve(__dirname, './core')
        },
        extensions: ['.js', '.ts', '.jsx', '.tsx', '.json', '.cjs.js', '.es.js'],
      },
      define: {
        __AUI_CONFIG__: JSON.stringify(merge({
          tagPrefix: 'aui',
          buildTime: new Date().toISOString()
        }, options || {}))
      },
    },
    isCustomElement: (tag: string) => tag.startsWith(`${tagPrefix}-`),
  }
}